import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createTask, deleteTask, getAllTasks, updateTask, getOneTask, getComments, createComment, archiveTask } from './api';
import { useParams } from 'react-router-dom';

//CREATE hook (post new task to api)
export function useCreateTask() {
  const queryClient = useQueryClient();
  const { plantId } = useParams();

  return useMutation({
    mutationFn: (task) => {
      return createTask({
        task: task.values,
        plantId,
        accessToken: task.accessToken,
      });
    },
    onSuccess: (response) => {
      // Optimistically add the new task to the cache so it appears immediately
      const newTask = response?.data || response;
      if (newTask) {
        queryClient.setQueryData(['plantTasks', plantId], (old) => {
          const prev = Array.isArray(old) ? old : [];
          return [...prev, newTask];
        });
      }

      // Invalidate and refetch to ensure data stays in sync with backend
      queryClient.invalidateQueries({ 
        queryKey: ['plantTasks', plantId],
        refetchType: 'active'
      });
    },
  });
}

//READ hook (get tasks from api)
export function useGetTasks({ accessToken }) {
  const { plantId } = useParams();
  return useQuery({
    queryKey: ['plantTasks', plantId],
    queryFn: () => getAllTasks({ plantId, accessToken }),
    enabled: accessToken !== null && !!plantId,
    refetchOnWindowFocus: false,
    staleTime: 0, // Always consider data stale to ensure refetch after mutations
  });
}

//READ hook for single task
export function useGetTask({ taskId, accessToken }) {
  return useQuery({
    queryKey: ['plantTask', taskId],
    queryFn: () => getOneTask({ taskId, accessToken }),
    enabled: accessToken !== null && !!taskId,
  });
}

//UPDATE hook (patch task in api)
export function useUpdateTask() {
  const queryClient = useQueryClient();
  const { plantId } = useParams();
  return useMutation({
    mutationFn: (task) =>
      updateTask({ task: task.values, accessToken: task.accessToken }),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['plantTasks', plantId],
        refetchType: 'active'
      });
    },
  });
}

//DELETE hook (delete task in api)
export function useDeleteTask() {
  const queryClient = useQueryClient();
  const { plantId } = useParams();
  return useMutation({
    mutationFn: ({ id, accessToken }) =>
      deleteTask({ taskId: id, accessToken }),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['plantTasks', plantId],
        refetchType: 'active'
      });
    },
  });
}

//ARCHIVE hook (archive task in api)
export function useArchiveTask() {
  const queryClient = useQueryClient();
  const { plantId } = useParams();
  return useMutation({
    mutationFn: ({ id, accessToken }) =>
      archiveTask({ id, accessToken }),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['plantTasks', plantId],
        refetchType: 'active'
      });
    },
  });
}

// Comments hooks
export function useGetComments({ taskId, accessToken }) {
  return useQuery({
    queryKey: ['taskComments', taskId],
    queryFn: () => getComments({ taskId, accessToken }),
    enabled: accessToken !== null && !!taskId,
    initialData: [],
  });
}

export function useCreateComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ comment, file, accessToken }) =>
      createComment({ comment, file, accessToken }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['taskComments', variables.comment.taskId] 
      });
    },
  });
}

